import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarDays, Clock, ChevronDown } from "lucide-react";
import { format, addDays, isToday, isTomorrow, isSameDay } from "date-fns";
import { startOfDay } from "date-fns";

interface DateTimePickerProps {
  selectedDate: Date;
  selectedTime: string;
  onDateTimeChange: (date: Date, time: string) => void;
  openingTime: string; // e.g. '10:00 AM'
  closingTime: string; // e.g. '9:30 PM'
  slotInterval?: number; // in minutes (default 15)
}

export function DateTimePicker({
  selectedDate,
  selectedTime,
  onDateTimeChange,
  openingTime,
  closingTime,
  slotInterval,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(selectedDate);
  const [tempTime, setTempTime] = useState("ASAP");

  // Generate time slots based on selected date
  const getTimeSlots = (date: Date) => {
    const now = new Date();

    // -----------------------------
    // Handle TODAY with ASAP logic
    // -----------------------------
    if (isToday(date)) {
      const roundedNow = roundUpToInterval(now, slotInterval || 30);
      const bufferMinutes = 30;
      const bufferedTime = new Date(
        roundedNow.getTime() + bufferMinutes * 60000
      );

      const allSlots = generateTimeSlots(
        date,
        openingTime,
        closingTime,
        slotInterval
      );

      const futureSlots = allSlots.filter((slot) => {
        const slotDate = timeStringToDate(now, slot);
        return slotDate >= bufferedTime;
      });

      return ["ASAP", ...futureSlots];
    }

    // -----------------------------
    // FUTURE DATE LOGIC
    // -----------------------------
    const openingDateTime = timeStringToDate(now, openingTime);

    // If current time is before opening → use opening time
    let startTime = openingTime;

    // If current time is after opening → use rounded current time
    if (now > openingDateTime) {
      const roundedNow = roundUpToInterval(now, slotInterval || 30);
      startTime = format(roundedNow, "hh:mm a");
    }

    return generateTimeSlots(date, openingTime, closingTime, slotInterval);
  };

  function roundUpToInterval(date: Date, interval: number) {
    const rounded = new Date(date);
    const minutes = date.getMinutes();
    const remainder = minutes % interval;

    if (remainder !== 0) {
      rounded.setMinutes(minutes + (interval - remainder));
    }

    rounded.setSeconds(0);
    rounded.setMilliseconds(0);
    return rounded;
  }

  const timeStringToDate = (date: Date, timeStr: string): Date => {
    const [time, meridian] = timeStr.split(" ");
    const [hour, minute] = time.split(":").map(Number);
    const hours24 = meridian === "PM" ? (hour % 12) + 12 : hour % 12;

    const newDate = new Date(date);
    newDate.setHours(hours24, minute, 0, 0);

    return newDate;
  };
  const generateTimeSlots = (
    date: Date,
    openingTime: string,
    closingTime: string,
    interval = 15
  ): string[] => {
    const start = timeStringToDate(date, openingTime);
    let end = timeStringToDate(date, closingTime);

    // Handle case where closing time is technically next day (i.e. after midnight)
    if (end <= start) {
      end.setDate(end.getDate() + 1); // move end to next day
    }

    const slots: string[] = [];
    const current = new Date(start);

    while (current <= end) {
      const hours = current.getHours();
      const minutes = current.getMinutes();
      const meridian = hours >= 12 ? "PM" : "AM";
      const displayHour = hours % 12 === 0 ? 12 : hours % 12;
      const displayMinutes = minutes.toString().padStart(2, "0");
      const formatted = `${displayHour}:${displayMinutes} ${meridian}`;

      slots.push(formatted);
      current.setMinutes(current.getMinutes() + interval);
    }

    return slots;
  };

  const timeSlots = getTimeSlots(tempDate);
  const getDateLabel = (date: Date) => {
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "MMM d, yyyy");
  };

  const handleConfirm = () => {
    // If user selects a future date but has ASAP selected, reset to first available time
    if (!isToday(tempDate) && tempTime === "ASAP") {
      const futureTimeSlots = getTimeSlots(tempDate);
      onDateTimeChange(tempDate, futureTimeSlots[0]);
    } else {
      onDateTimeChange(tempDate, tempTime);
    }
    setIsOpen(false);
  };

  // Update time when date changes
  const handleDateChange = (date: Date | undefined) => {
    if (!date) return;
    setTempDate(date);

    // If switching to today and current time is not ASAP, keep current time
    // If switching to future date and current time is ASAP, change to first regular time
    if (!isToday(date) && tempTime === "ASAP") {
      const futureTimeSlots = getTimeSlots(date);
      setTempTime(futureTimeSlots[0]);
    } else if (isToday(date) && !getTimeSlots(date).includes(tempTime)) {
      setTempTime("ASAP");
    }
  };
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="justify-between min-w-[220px] h-14 bg-white hover:bg-gray-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900 text-sm">
                {getDateLabel(selectedDate)}
              </div>
              <div className="text-xs text-gray-500">
                {selectedTime === "ASAP" ? "As Soon As Possible" : selectedTime}
              </div>
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="w-[95vw] max-w-lg mx-auto p-4 sm:p-6 
                          max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3 text-lg sm:text-xl">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <CalendarDays className="w-4 h-4 text-white" />
            </div>
            <span className="font-display">Select Date & Time</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 sm:space-y-6">
          {/* Date Selection */}
          <div>
            <h3 className="font-semibold mb-3 sm:mb-4 flex items-center space-x-2 text-gray-900 text-sm sm:text-base">
              <CalendarDays className="w-4 h-4 text-blue-600" />
              <span>Choose Date</span>
            </h3>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={tempDate}
                onSelect={handleDateChange}
                disabled={(date) => date < startOfDay(new Date())}
                className="rounded-xl border border-gray-200 bg-white shadow-sm scale-90 sm:scale-100"
              />
            </div>
          </div>

          {/* Time Selection */}
          <div>
            <h3 className="font-semibold mb-3 sm:mb-4 flex items-center space-x-2 text-gray-900 text-sm sm:text-base">
              <Clock className="w-4 h-4 text-blue-600" />
              <span>
                Choose Time{" "}
                {isToday(tempDate) && (
                  <span className="text-emerald-600">(ASAP Available)</span>
                )}
              </span>
            </h3>
            <Select value={tempTime} onValueChange={setTempTime}>
              <SelectTrigger className="h-12 border-gray-200">
                <SelectValue placeholder="Select time" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {timeSlots.map((time) => (
                  <SelectItem
                    key={time}
                    value={time}
                    className={`font-medium ${
                      time === "ASAP" ? "bg-emerald-50 text-emerald-700" : ""
                    }`}
                  >
                    {time === "ASAP" ? (
                      <div className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                        <span>ASAP (As Soon As Possible)</span>
                      </div>
                    ) : (
                      time
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* ASAP Info */}
            {isToday(tempDate) && (
              <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center space-x-2 text-emerald-800 text-sm">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">
                    ASAP orders are prepared immediately and delivered in 25-35
                    minutes
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Button */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-2 sm:pt-4">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="flex-1 h-12"
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} className="flex-1 h-12 btn-primary">
              Confirm Selection
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
